import { Pressable, View } from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { Check, ChevronRight, Pencil, Plus, TriangleAlert } from 'lucide-react-native';
import { colors, formatMinutes, formatTime, pick, sumMinutes, weekdayName, weekdayShort, type Weekday } from '@hogar/shared';
import { Button, Card, Screen, Txt, notice, useT, showProblem } from '../../components/ui';
import { BackHeader } from '../../components/BackHeader';
import { api } from '../../lib/api';
import { useAction } from '../../lib/session';
import { useHouse } from '../../lib/house';

export default function RoomDetail() {
  const { id, day } = useLocalSearchParams<{ id: string; day?: string }>();
  const { t, lang } = useT();
  const { rooms, tasks, today, plans } = useHouse();
  const act = useAction();
  const room = rooms.find((r) => r.id === id);
  if (!room) return <Screen title="" />;
  const all = tasks.filter((tk) => tk.roomId === id).sort((a, b) => a.order - b.order);
  const selectedDay = (day !== undefined ? Number(day) : today.weekday) as Weekday;
  const onDay = all.filter((tk) => tk.days.includes(selectedDay));
  const viewingToday = selectedDay === today.weekday;
  // Today's checks follow today's plan, which includes tasks moved here from another day.
  const todayPlan = new Set(plans[today.date]?.[room.crew] ?? []);
  // Keeps names lined up when only some rows have a box to check.
  const anyBox = viewingToday && all.some((tk) => todayPlan.has(tk.id));

  const toggle = (taskId: string, done: boolean) =>
    act(() => (done ? api('DELETE', `/results/${taskId}`) : api('POST', '/results', { taskId, status: 'done' }))).catch(() => showProblem(t('errGeneric'), lang));

  return (
    <Screen>
      <View style={{ flexDirection: 'row', alignItems: 'flex-end' }}>
        <View style={{ flex: 1 }}>
          <BackHeader
            title={pick(room.name, lang)}
            subtitle={`${formatMinutes(sumMinutes(onDay))} · ${onDay.length} ${onDay.length === 1 ? t('task') : t('tasks')}`}
            fallback="/rutinas"
          />
        </View>
        <Pressable
          onPress={() => router.push({ pathname: '/room-edit', params: { id: room.id } })}
          hitSlop={10}
          accessibilityLabel={t('editRoom')}
          style={{ marginBottom: 22, padding: 8, borderRadius: 20, backgroundColor: colors.primarySoft }}
        >
          <Pencil size={18} color={colors.primary} />
        </Pressable>
      </View>
      {all.length > onDay.length && (
        <Txt size={13} color={colors.textMuted} style={{ marginBottom: 10 }}>
          {t(viewingToday ? 'roomTodayHint' : 'roomDayHint', { day: weekdayName(selectedDay, lang) })}
        </Txt>
      )}
      {all.length > 0 && (
        <Card>
          {all.map((tk, i) => {
            const isToday = viewingToday && todayPlan.has(tk.id);
            const scheduled = tk.days.includes(selectedDay) || isToday;
            const r = isToday ? today.results[tk.id] : undefined;
            return (
              <View key={tk.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 64, borderBottomWidth: i === all.length - 1 ? 0 : 1, borderBottomColor: colors.divider, opacity: scheduled ? 1 : 0.5 }}>
                {isToday ? (
                  <Pressable
                    accessibilityLabel={r ? t('markUndone') : t('markDone')}
                    onPress={() => toggle(tk.id, !!r)}
                    hitSlop={8}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      borderWidth: 2,
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderColor: r?.status === 'could_not' ? colors.warning : r ? colors.primary : '#CFD6D0',
                      backgroundColor: r?.status === 'could_not' ? colors.warningSoft : r ? colors.primary : colors.white,
                    }}
                  >
                    {r?.status === 'done' && <Check size={16} color={colors.white} strokeWidth={3} />}
                    {r?.status === 'could_not' && <TriangleAlert size={14} color="#A8690F" />}
                  </Pressable>
                ) : anyBox ? (
                  <View style={{ width: 28 }} />
                ) : null}
                <Pressable style={{ flex: 1, flexDirection: 'row', alignItems: 'center', paddingVertical: 10 }} onPress={() => router.push({ pathname: '/task/[id]', params: { id: tk.id } })}>
                  <View style={{ flex: 1 }}>
                    <Txt weight="medium">{pick(tk.name, lang)}</Txt>
                    <Txt size={13} color={colors.textMuted}>
                      {tk.minutes} min · {tk.days.length ? tk.days.map((d) => weekdayShort(d, lang)).join(' · ') : '—'}
                    </Txt>
                    {!scheduled ? (
                      <Txt size={12} color={colors.textMuted}>
                        {viewingToday ? t('notToday') : t('notOnDay', { day: weekdayName(selectedDay, lang) })}
                      </Txt>
                    ) : null}
                    {r ? (
                      <Txt size={12} color={r.status === 'done' ? colors.primary : colors.warningText}>
                        {t('checkedAt', { time: formatTime(r.at) })} {t('checkedBy', { name: r.byName })}
                      </Txt>
                    ) : null}
                  </View>
                  <ChevronRight size={18} color={colors.textFaint} />
                </Pressable>
              </View>
            );
          })}
        </Card>
      )}
      <Button label={t('addTask')} Icon={Plus} variant="outline" onPress={() => router.push({ pathname: '/task/[id]', params: { id: 'new', roomId: room.id } })} />
      {viewingToday && all.some((tk) => todayPlan.has(tk.id)) && (
        <Txt size={13} color={colors.textMuted} style={{ marginTop: 12 }}>
          {t('ownerCanCheck')}
        </Txt>
      )}
    </Screen>
  );
}
